import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import {
    PaperAgentCommitTextToMemoryInput,
    PaperAgentGetDocumentAnalysisStatusInput,
    PaperAgentStartDocumentAnalysisInput
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import Alert from '@amzn/meridian/alert';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import FileInput from '@amzn/meridian/file-input';
import Input from '@amzn/meridian/input';
import Loader from '@amzn/meridian/loader';
import Modal from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';

import { isNil } from 'lodash';
import { contractConfigurationClient } from 'src/api/client';
import { uploadToS3 } from 'src/components/AI/DocumentAnalysis/upload-to-s3';
import { AppContext } from 'src/contexts/appContext';
import { formatBytes } from 'src/util/format-bytes/format-bytes';

interface IUploadDocumentButton {
    conversationId: string;
}

enum JobStatus {
    NOT_STARTED = 'NOT_STARTED',
    IN_PROGRESS = 'IN_PROGRESS',
    SUCCEEDED = 'SUCCEEDED',
    FAILED = 'FAILED',
    PARTIAL_SUCCESS = 'PARTIAL_SUCCESS'
}

const ALLOWED_EXTENSIONS = new Set(['pdf']);

export const UploadDocumentButton: React.FC<IUploadDocumentButton> = (props) => {
    const { conversationId } = props;
    const { userInfo } = useContext(AppContext);
    const [file, setFile] = useState<File | undefined>();
    const [working, setWorking] = useState(false);
    const [error, setError] = useState(false);
    const [progress, setProgress] = useState<number | undefined>(0);
    const [polling, setPolling] = useState<any>();
    const [jobStatus, setJobStatus] = useState<JobStatus>(JobStatus.NOT_STARTED);
    const [commitPayload, setCommitPayload] = useState<PaperAgentCommitTextToMemoryInput | undefined>();
    const [status, setStatus] = useState('Ready to submit');
    const [documentName, setDocumentName] = useState('');

    const [isValidName, errorMessage] = useMemo(() => {
        const parts = documentName.split('.');
        const extension = parts[parts.length - 1];
        if (ALLOWED_EXTENSIONS.has(extension)) {
            return [true, undefined];
        } else {
            const allowed = Array.from(ALLOWED_EXTENSIONS).join(', ');
            return [false, `Invalid extension ${extension}. Allowed types: ${allowed}`];
        }
    }, [documentName]);

    const pollingStatuses = new Set([JobStatus.IN_PROGRESS, JobStatus.NOT_STARTED]);

    useEffect(() => {
        if (!isNil(polling) && !pollingStatuses.has(jobStatus)) {
            setPolling((prev: any) => {
                if (!isNil(prev)) {
                    clearInterval(prev);
                }
                return undefined;
            });
        }

        if (jobStatus === JobStatus.FAILED) {
            setError(true);
        }
    }, [jobStatus, polling]);

    useEffect(() => {
        if (!isNil(commitPayload)) {
            setStatus('Adding to agent memory');

            contractConfigurationClient
                .paperAgentCommitTextToMemory(commitPayload)
                .toPromise()
                .then(() => {
                    setStatus('Success!');
                    setProgress(100);
                });
        }
    }, [commitPayload]);

    const pollForUpdates = useCallback(
        async (jobId: string) => {
            const response = await contractConfigurationClient
                .paperAgentGetDocumentAnalysisStatus(
                    PaperAgentGetDocumentAnalysisStatusInput.clone({
                        jobId: jobId
                    })
                )
                .toPromise()
                .catch((err) => {
                    console.error(err);
                    return { status: JobStatus.FAILED };
                });
            setJobStatus(response.status as JobStatus);
            setStatus(`Processing file: ${response.status}`);

            if (response.status === JobStatus.SUCCEEDED) {
                setProgress((prev) => Math.max(prev ?? 0, 75));
                setCommitPayload(
                    PaperAgentCommitTextToMemoryInput.clone({
                        jobId: jobId,
                        conversationId: conversationId,
                        documentName: documentName,
                        userId: userInfo.username
                    })
                );
            }
        },
        [file, conversationId, userInfo]
    );

    const onUpload = useCallback(async () => {
        if (isNil(file)) {
            return;
        }

        setError(false);
        setWorking(true);
        setStatus('Uploading to agent');
        setProgress(0);

        let documentKey;
        try {
            documentKey = await uploadToS3(documentName, userInfo.username, file);
        } catch (e) {
            setStatus(`${e}`);
            setWorking(false);
            setError(true);
            return;
        }

        setProgress(25);
        setJobStatus(JobStatus.NOT_STARTED);

        const response = await contractConfigurationClient
            .paperAgentStartDocumentAnalysis(
                PaperAgentStartDocumentAnalysisInput.clone({
                    conversationId: conversationId,
                    s3Key: documentKey,
                    userId: userInfo.username,
                    documentName: documentName
                })
            )
            .toPromise()
            .catch((err) => {
                setError(true);
                setStatus(`Error: ${err}`);
                return undefined;
            });

        if (isNil(response)) {
            return;
        }

        const jobId = response.jobId;

        setProgress(50);
        setStatus('Processing file');

        setPolling((prev: any) => {
            if (!isNil(prev)) {
                clearInterval(prev);
            }
            return setInterval(pollForUpdates, 5000, jobId);
        });
    }, [file, conversationId, userInfo, pollForUpdates, documentName]);

    const onFileAttached = useCallback(
        (acceptedFiles: File[]) => {
            const newFile = acceptedFiles[0];
            setProgress(0);
            setFile(newFile);
            setDocumentName(newFile.name);
        },
        [setFile]
    );

    const cancelUpload = useCallback(() => {
        if (!isNil(file)) {
            setFile(undefined);
            setStatus('Ready to submit');
            setWorking(false);
            setProgress(0);
        }
    }, [file]);

    return (
        <>
            <FileInput
                type={'single'}
                onFileAttached={onFileAttached}
                uploadButtonLabel={'Attach File'}
                uploadButtonSize={'small'}
                data-testid={'file-input'}
            />
            <Modal open={!isNil(file)} onClose={cancelUpload} title={`Send file to agent`} width={'80vh'}>
                <Column>
                    <Input
                        prefix={'File name'}
                        value={documentName}
                        onChange={setDocumentName}
                        error={!isValidName}
                        errorMessage={errorMessage}
                    />
                    <Row widths={['fit', 'fill']}>
                        <Row>{!isNil(file?.size) && <Text>Size: {formatBytes(file!.size)}</Text>}</Row>
                        <Loader type={'linear'} percentage={progress} showPercentageText={false} />
                    </Row>
                    <Alert type={error ? 'error' : progress == 100 ? 'success' : 'neutral'}>{status}</Alert>
                    <Button onClick={onUpload} disabled={working || !isValidName}>
                        Send to Agent
                    </Button>
                </Column>
            </Modal>
        </>
    );
};
